import { config } from '../config';
import { getToken } from '../utils/token';
import { getDatacentersHeader, flattenBroadcastResponse } from '../utils/datacenter';
import { throwHttpError } from '../utils/httpErrors';

export interface ChartClearRecord {
  id?: number;
  created: string;
  currency: string;

  clearedEarn: number;
  clearedInvoiceAmount: number;
  clearedInvoiceCount: number;
  clearedTakerCount: number;
  clearedYield: number;
  clearedYieldWeightedSum?: number;
  clearedAprWeightedSum?: number;
  clearedDpeWeightedSum?: number;

  acceptedEarn: number;
  acceptedInvoiceAmount: number;
  acceptedInvoiceCount: number;
  acceptedTakerCount: number;
  acceptedYield: number;
  acceptedYieldWeightedSum?: number;
  acceptedAprWeightedSum?: number;
  acceptedDpeWeightedSum?: number;

  awardedEarn: number;
  awardedInvoiceAmount: number;
  awardedInvoiceCount: number;
  awardedTakerCount: number;
  awardedYield: number;
  awardedYieldWeightedSum?: number;
  awardedAprWeightedSum?: number;
  awardedDpeWeightedSum?: number;

  marketUuid?: string;
  makerUuid?: string;
  makerDivisionName?: string;
  marketType?: string;

  exchangeRates?: Record<string, number>;
  exchangeRate?: number;
  marketReturnAmount?: number;
  bankingDays?: number;

  day?: Date;
  week?: Date;
  month?: Date;
  quarter?: Date;
  year?: Date;
}

export interface ChartDataPoint {
  date: Date;
  yield: number;
  revenue: number;
  divisionsAwarded: number;
  clearedInvoiceAmount: number;
  weightedAvgDesiredApr: number;
  records: ChartClearRecord[];
}

export interface CurrencyBreakdown {
  currency: string;
  currencyOriginal: string;
  clearedEarn: number;
  clearedEarnOriginal: number;
  clearedYield: number;
}

export interface CompanyBreakdown {
  makerUuid: string;
  makerDivisionName: string;
  clearedEarn: number;
  clearedYield: number;
}

export interface TimeFrameOption {
  label: string;
  value: string;
  startDate: Date;
  endDate?: Date;
}

export interface GroupByOption {
  label: string;
  value: 'day' | 'week' | 'month' | 'quarter' | 'year' | 'all';
}

function getStartOfDay(): Date {
  const date = new Date();
  date.setUTCHours(0, 0, 0, 0);
  return date;
}

function subtractDays(days: number): Date {
  const date = getStartOfDay();
  date.setUTCDate(date.getUTCDate() - days);
  return date;
}

function getStartOfQuarter(): Date {
  const date = new Date();
  const quarter = Math.floor(date.getUTCMonth() / 3);
  return new Date(Date.UTC(date.getUTCFullYear(), quarter * 3, 1, 0, 0, 0, 0));
}

function getStartOfYear(): Date {
  const date = new Date();
  return new Date(Date.UTC(date.getUTCFullYear(), 0, 1, 0, 0, 0, 0));
}

function subtractYears(years: number): Date {
  const date = new Date();
  return new Date(Date.UTC(date.getUTCFullYear() - years, date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0));
}

export function getTimeFrameOptions(): TimeFrameOption[] {
  return [
    { label: 'Past 7 Days', value: 'past7days', startDate: subtractDays(7) },
    { label: 'Past 30 Days', value: 'past30days', startDate: subtractDays(30) },
    { label: 'Past 60 Days', value: 'past60days', startDate: subtractDays(60) },
    { label: 'This Quarter', value: 'thisQuarter', startDate: getStartOfQuarter() },
    { label: 'This Year', value: 'thisYear', startDate: getStartOfYear() },
    { label: 'Past Year', value: 'pastYear', startDate: subtractYears(1) },
  ];
}

export function getGroupByOptions(): GroupByOption[] {
  return [
    { label: 'Day', value: 'day' },
    { label: 'Week', value: 'week' },
    { label: 'Month', value: 'month' },
    { label: 'Quarter', value: 'quarter' },
    { label: 'Year', value: 'year' },
    { label: 'All', value: 'all' },
  ];
}

function formatDateForApi(date: Date): string {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const seconds = String(date.getUTCSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

function normalizeRecord(record: ChartClearRecord): ChartClearRecord {
  const normalized = { ...record };

  if (!normalized.clearedEarn) {
    normalized.acceptedEarn = normalized.acceptedEarn || 0;
    normalized.awardedEarn = normalized.awardedEarn || 0;
    normalized.clearedEarn = normalized.acceptedEarn + normalized.awardedEarn;
  }

  if (!normalized.clearedInvoiceAmount) {
    normalized.acceptedInvoiceAmount = normalized.acceptedInvoiceAmount || 0;
    normalized.awardedInvoiceAmount = normalized.awardedInvoiceAmount || 0;
    normalized.clearedInvoiceAmount = normalized.acceptedInvoiceAmount + normalized.awardedInvoiceAmount;
  }

  if (!normalized.clearedInvoiceCount) {
    normalized.acceptedInvoiceCount = normalized.acceptedInvoiceCount || 0;
    normalized.awardedInvoiceCount = normalized.awardedInvoiceCount || 0;
    normalized.clearedInvoiceCount = normalized.acceptedInvoiceCount + normalized.awardedInvoiceCount;
  }

  if (!normalized.clearedTakerCount) {
    normalized.acceptedTakerCount = normalized.acceptedTakerCount || 0;
    normalized.awardedTakerCount = normalized.awardedTakerCount || 0;
    normalized.clearedTakerCount = normalized.acceptedTakerCount + normalized.awardedTakerCount;
  }

  if (!normalized.clearedDpeWeightedSum) {
    normalized.acceptedDpeWeightedSum = normalized.acceptedDpeWeightedSum || 0;
    normalized.awardedDpeWeightedSum = normalized.awardedDpeWeightedSum || 0;
    normalized.clearedDpeWeightedSum = normalized.acceptedDpeWeightedSum + normalized.awardedDpeWeightedSum;
  }

  const clearedYield = normalized.clearedYield || 0;
  const acceptedYield = normalized.acceptedYield || 0;
  const awardedYield = normalized.awardedYield || 0;
  normalized.clearedYieldWeightedSum = clearedYield * (normalized.clearedInvoiceAmount || 0);
  normalized.acceptedYieldWeightedSum = acceptedYield * (normalized.acceptedInvoiceAmount || 0);
  normalized.awardedYieldWeightedSum = awardedYield * (normalized.awardedInvoiceAmount || 0);

  normalized.bankingDays = normalized.bankingDays || 360;

  // Use local time for date grouping to match old admin's moment.local() behavior
  const created = new Date(normalized.created);
  normalized.day = new Date(created.getFullYear(), created.getMonth(), created.getDate());

  const dayOfWeek = created.getDay();
  const weekStart = new Date(created);
  weekStart.setDate(created.getDate() - dayOfWeek);
  normalized.week = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate());

  normalized.month = new Date(created.getFullYear(), created.getMonth(), 1);

  const quarter = Math.floor(created.getMonth() / 3);
  normalized.quarter = new Date(created.getFullYear(), quarter * 3, 1);

  normalized.year = new Date(created.getFullYear(), 0, 1);

  return normalized;
}

export async function fetchPerformanceHistory(
  startDate: Date,
  currency: string,
  endDate?: Date,
  marketUuid?: string,
): Promise<ChartClearRecord[]> {
  const token = getToken();
  if (!token) {
    throw new Error('Authentication required. Please log in.');
  }

  const url = `${config.GATEWAY_URL}/broadcast/api/admin/market/clears`;
  const params = new URLSearchParams({
    startDate: formatDateForApi(startDate),
    exchangeRateCurrency: currency,
  });

  if (endDate) {
    params.append('endDate', formatDateForApi(endDate));
  }

  if (marketUuid) {
    params.append('marketUuid', marketUuid);
  }

  const dcHeader = getDatacentersHeader();
  let response: Response;

  try {
    response = await fetch(`${url}?${params.toString()}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...(dcHeader !== null ? { 'x-c2fo-dc': dcHeader } : {}),
      },
    });
  } catch (networkError) {
    if (networkError instanceof TypeError && networkError.message.includes('fetch')) {
      throw new Error('Unable to connect to the API server. Please check your network connection.');
    }

    throw new Error(`Network error: ${networkError instanceof Error ? networkError.message : 'Unknown error'}`);
  }

  if (!response.ok) {
    throwHttpError(response.status, 'performance history');
  }

  let rawData: unknown;

  try {
    rawData = await response.json();
  } catch {
    throw new Error('Invalid response format from the API server.');
  }

  let data: ChartClearRecord[];
  if (Array.isArray(rawData)) {
    data = flattenBroadcastResponse(rawData as ChartClearRecord[]);
  } else if (
    rawData &&
    typeof rawData === 'object' &&
    'data' in rawData &&
    Array.isArray((rawData as { data: unknown }).data)
  ) {
    const innerData = (rawData as { data: unknown }).data as ChartClearRecord[];
    data = flattenBroadcastResponse(innerData);
  } else {
    data = [];
  }

  return data.map(normalizeRecord);
}

/**
 * Convert local date to UTC for Highcharts with useUTC: true
 * This matches old admin's: moment.parseZone(created).local().startOf('day').utc().toDate()
 * The result is a Date where the UTC date matches the local date of the input
 */
export function localDateToUtcForChart(localDate: Date): Date {
  // Create a UTC date with the same year/month/day as the local date
  return new Date(Date.UTC(localDate.getFullYear(), localDate.getMonth(), localDate.getDate()));
}

export function reduceByTimeFrame(
  records: ChartClearRecord[],
  groupBy: GroupByOption['value'],
  targetCurrency = 'USD',
): ChartDataPoint[] {
  if (records.length === 0) {
    return [];
  }

  if (groupBy === 'all') {
    const aggregated = aggregateRecords(records, targetCurrency);
    const firstDate = records[0].day ?? new Date(records[0].created);
    return [
      {
        date: localDateToUtcForChart(firstDate),
        yield: aggregated.clearedYield,
        revenue: aggregated.clearedEarn,
        divisionsAwarded: aggregated.clearedTakerCount,
        clearedInvoiceAmount: aggregated.clearedInvoiceAmount,
        weightedAvgDesiredApr: aggregated.weightedAvgDesiredApr,
        records,
      },
    ];
  }

  const grouped = new Map<string, ChartClearRecord[]>();

  for (const record of records) {
    const groupDate = record[groupBy];
    if (!groupDate) continue;

    const key = groupDate.toISOString();
    if (!grouped.has(key)) {
      grouped.set(key, []);
    }

    grouped.get(key)!.push(record);
  }

  const result: ChartDataPoint[] = [];

  for (const [dateKey, groupRecords] of grouped) {
    const aggregated = aggregateRecords(groupRecords, targetCurrency);
    const localDate = new Date(dateKey);
    result.push({
      // Convert local date to UTC for Highcharts
      date: localDateToUtcForChart(localDate),
      yield: aggregated.clearedYield,
      revenue: aggregated.clearedEarn,
      divisionsAwarded: aggregated.clearedTakerCount,
      clearedInvoiceAmount: aggregated.clearedInvoiceAmount,
      weightedAvgDesiredApr: aggregated.weightedAvgDesiredApr,
      records: groupRecords,
    });
  }

  return result.sort((a, b) => a.date.getTime() - b.date.getTime());
}

interface AggregatedStats {
  clearedEarn: number;
  clearedInvoiceAmount: number;
  clearedInvoiceCount: number;
  clearedTakerCount: number;
  clearedYield: number;
  weightedAvgDesiredApr: number;
}

function aggregateRecords(records: ChartClearRecord[], targetCurrency = 'USD'): AggregatedStats {
  let clearedEarn = 0;
  let clearedInvoiceAmount = 0;
  let clearedInvoiceCount = 0;
  let clearedTakerCount = 0;
  let acceptedYieldWeightedSum = 0;
  let awardedYieldWeightedSum = 0;
  let acceptedInvoiceAmount = 0;
  let awardedInvoiceAmount = 0;
  let weightedAvgDesiredAprSum = 0;

  for (const record of records) {
    // Get exchange rate for currency conversion
    // If record currency matches target, rate is 1
    // Otherwise use exchangeRates[targetCurrency] or exchangeRate
    let exchangeRate = 1;
    if (record.currency !== targetCurrency) {
      if (record.exchangeRates && record.exchangeRates[targetCurrency]) {
        exchangeRate = record.exchangeRates[targetCurrency];
      } else if (record.exchangeRate) {
        exchangeRate = record.exchangeRate;
      }
    }

    // Apply exchange rate to currency fields (matching old admin _sumCurrencyStats)
    clearedEarn += (record.clearedEarn ?? 0) * exchangeRate;
    clearedInvoiceAmount += (record.clearedInvoiceAmount ?? 0) * exchangeRate;
    clearedInvoiceCount += record.clearedInvoiceCount ?? 0;
    clearedTakerCount += record.clearedTakerCount ?? 0;

    // Yield weighted sums also need exchange rate for proper averaging across currencies
    acceptedYieldWeightedSum += (record.acceptedYieldWeightedSum ?? 0) * exchangeRate;
    awardedYieldWeightedSum += (record.awardedYieldWeightedSum ?? 0) * exchangeRate;
    acceptedInvoiceAmount += (record.acceptedInvoiceAmount ?? 0) * exchangeRate;
    awardedInvoiceAmount += (record.awardedInvoiceAmount ?? 0) * exchangeRate;

    if (record.marketReturnAmount && record.clearedInvoiceAmount) {
      weightedAvgDesiredAprSum += record.marketReturnAmount * record.clearedInvoiceAmount * exchangeRate;
    }
  }

  let clearedYield = 0;
  const totalInvoiceAmount = acceptedInvoiceAmount + awardedInvoiceAmount;
  if (totalInvoiceAmount > 0) {
    clearedYield = Math.round(((acceptedYieldWeightedSum + awardedYieldWeightedSum) / totalInvoiceAmount) * 100) / 100;
  }

  let weightedAvgDesiredApr = 0;
  if (clearedInvoiceAmount > 0) {
    weightedAvgDesiredApr = Math.round((weightedAvgDesiredAprSum / clearedInvoiceAmount) * 100) / 100;
  }

  return {
    clearedEarn,
    clearedInvoiceAmount,
    clearedInvoiceCount,
    clearedTakerCount,
    clearedYield,
    weightedAvgDesiredApr,
  };
}

export function getRecommendedGroupBy(startDate: Date, endDate?: Date): GroupByOption['value'] {
  const end = endDate ?? new Date();
  const diffMs = Math.abs(end.getTime() - startDate.getTime());
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays > 720) {
    return 'month';
  } else if (diffDays > 220) {
    return 'week';
  }

  return 'day';
}

export function formatDateForGroupBy(date: Date, groupBy: GroupByOption['value'], useShortFormat = false): string {
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const monthsShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  const day = date.getUTCDate();
  const dayOfWeek = date.getUTCDay();

  switch (groupBy) {
    case 'month':
      return `${months[month]} ${year}`;
    case 'quarter': {
      const quarter = Math.floor(month / 3) + 1;
      return `Q${quarter} ${year}`;
    }

    case 'year':
      return `${year}`;
    default:
      if (useShortFormat) {
        return `${monthsShort[month]} ${day}, ${year}`;
      }

      return `${days[dayOfWeek]}, ${months[month]} ${day}, ${year}`;
  }
}

export function formatCurrencyShort(value: number): string {
  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (absValue >= 1e9) {
    return `${sign}$${(absValue / 1e9).toFixed(1)}B`;
  } else if (absValue >= 1e6) {
    return `${sign}$${(absValue / 1e6).toFixed(1)}M`;
  } else if (absValue >= 1e3) {
    return `${sign}$${(absValue / 1e3).toFixed(1)}K`;
  }

  return `${sign}$${absValue.toFixed(0)}`;
}

export function formatCurrency(value: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export function reduceByCurrency(records: ChartClearRecord[], targetCurrency = 'USD'): CurrencyBreakdown[] {
  const grouped = new Map<string, ChartClearRecord[]>();

  for (const record of records) {
    const currency = record.currency ?? 'USD';
    if (!grouped.has(currency)) {
      grouped.set(currency, []);
    }

    grouped.get(currency)!.push(record);
  }

  const result: CurrencyBreakdown[] = [];

  for (const [currency, currencyRecords] of grouped) {
    // For currency breakdown, aggregate with exchange rate conversion
    const aggregated = aggregateRecords(currencyRecords, targetCurrency);

    // clearedEarnOriginal is the raw sum in original currency (no exchange rate conversion)
    // This matches old admin's clearedEarnOriginal from _sumCurrencyStats
    const clearedEarnOriginal = currencyRecords.reduce((sum, r) => {
      return sum + (r.clearedEarn ?? 0);
    }, 0);

    result.push({
      currency: targetCurrency,
      currencyOriginal: currency,
      clearedEarn: aggregated.clearedEarn,
      clearedEarnOriginal,
      clearedYield: aggregated.clearedYield,
    });
  }

  return result.sort((a, b) => b.clearedEarn - a.clearedEarn);
}

export function reduceByCompany(records: ChartClearRecord[], targetCurrency = 'USD'): CompanyBreakdown[] {
  const grouped = new Map<string, ChartClearRecord[]>();

  for (const record of records) {
    const makerUuid = record.makerUuid ?? 'unknown';
    if (!grouped.has(makerUuid)) {
      grouped.set(makerUuid, []);
    }

    grouped.get(makerUuid)!.push(record);
  }

  const result: CompanyBreakdown[] = [];

  for (const [makerUuid, companyRecords] of grouped) {
    const aggregated = aggregateRecords(companyRecords, targetCurrency);
    result.push({
      makerUuid,
      makerDivisionName: companyRecords[0]?.makerDivisionName ?? 'Unknown',
      clearedEarn: aggregated.clearedEarn,
      clearedYield: aggregated.clearedYield,
    });
  }

  return result.sort((a, b) => b.clearedEarn - a.clearedEarn);
}
